import { observer } from 'mobx-react';
import { FC, useState } from 'react';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Checkbox } from '@monorepo/base/src/components/form/checkbox/checkbox';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { AdminxStore } from '../../../modules/stores';
import { DomainMaxLossApi } from '../../../modules/apis/domain-max-loss.api';
import { DomainMaxLossCacheEntry } from '../../../modules/models/domain-max-loss.model';
import styles from './domain-max-loss.module.scss';

type DomainMaxLossRow = DomainMaxLossCacheEntry & {
	isSelected: boolean;
};

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'domain_max_loss_pagebar')] };

function parseCampaignIdInput(value: string): number | undefined {
	const trimmed = value.trim();
	if (!trimmed) {
		return undefined;
	}
	const n = Number(trimmed);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}

export const DomainMaxLossPage: FC = observer(() => {
	const { toastsStore } = useStores<AdminxStore>();
	const [campaignIdInput, setCampaignIdInput] = useState<string>('');
	const [domainInput, setDomainInput] = useState<string>('');
	const [rows, setRows] = useState<DomainMaxLossRow[]>([]);
	const [hasChecked, setHasChecked] = useState(false);
	const [error, setError] = useState<string>('');
	const [isLoadingStatus, setIsLoadingStatus] = useState(false);
	const [isLoadingClear, setIsLoadingClear] = useState(false);
	const [selectAll, setSelectAll] = useState(false);

	const selectedCount = rows.filter(row => row.isSelected).length;

	const handleCheckStatus = () => {
		setIsLoadingStatus(true);
		setError('');

		const campaignId = parseCampaignIdInput(campaignIdInput);
		if (campaignIdInput.trim() && campaignId === undefined) {
			setIsLoadingStatus(false);
			setError('Campaign ID must be a valid positive number');
			return;
		}

		const domain = domainInput.trim() || undefined;
		const params: { campaign_id?: number; domain?: string } = {};
		if (campaignId !== undefined) {
			params.campaign_id = campaignId;
		}
		if (domain) {
			params.domain = domain;
		}

		DomainMaxLossApi.getDomainMaxLossStatus(params)
			.then(res => {
				const list = Array.isArray(res) ? res : [];
				setRows(list.map(entry => ({ ...entry, isSelected: false })));
				setHasChecked(true);
				setSelectAll(false);
			})
			.catch(() => {
				setRows([]);
				setHasChecked(false);
				setError('Failed to fetch paused domains cache status');
				toastsStore.addToast({
					msg: 'Failed to fetch paused domains cache status',
					type: DispatchLogsTypes.Error,
				});
			})
			.finally(() => {
				setIsLoadingStatus(false);
			});
	};

	const handleSelectAll = (nextSelectAll: boolean) => {
		setSelectAll(nextSelectAll);
		setRows(prev => prev.map(row => ({ ...row, isSelected: nextSelectAll })));
	};

	const handleClearCache = () => {
		const selectedKeys = rows.filter(row => row.isSelected).map(row => row.key);
		if (selectedKeys.length === 0) {
			return;
		}

		setIsLoadingClear(true);
		DomainMaxLossApi.unPauseDomains(selectedKeys)
			.then(() => {
				toastsStore.addToast({
					msg: `${selectedKeys.length} item(s) removed from paused domains cache.`,
					type: DispatchLogsTypes.Success,
				});
				setRows(prev => prev.filter(row => !selectedKeys.includes(row.key)));
				setSelectAll(false);
			})
			.catch(() => {
				toastsStore.addToast({
					msg: 'Failed to clear paused domains cache',
					type: DispatchLogsTypes.Error,
				});
			})
			.finally(() => {
				setIsLoadingClear(false);
			});
	};

	return (
		<Disable is={isLoadingClear}>
			<Page>
				<Pagebar debugProps={pageBarDebugProps}>
					<PagebarTitle>Paused Domains Cache</PagebarTitle>
				</Pagebar>
				<div className={styles.wrapper}>
					<div className={styles.searchRow}>
						<Input
							topLabel="Campaign ID (optional)"
							type="number"
							value={campaignIdInput}
							onValue={value => {
								if (error) {
									setError('');
								}
								setCampaignIdInput(value);
							}}
							className={styles.campaignIdField}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'domain_max_loss_campaign_id')] }}
						/>
						<Input
							topLabel="Domain (optional)"
							value={domainInput}
							onValue={value => {
								if (error) {
									setError('');
								}
								setDomainInput(value);
							}}
							className={styles.domainField}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'domain_max_loss_domain')] }}
						/>
						<PrimaryButton
							disabled={isLoadingStatus}
							isRotateIcon={isLoadingStatus}
							onClick={handleCheckStatus}
							className={styles.checkStatusBtn}
							icon={'search-sm'}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'domain_max_loss_check_status')] }}>
							Check Status
						</PrimaryButton>
					</div>
					{error ? <div className={styles.errorMsg}>{error}</div> : null}
					{hasChecked && rows.length === 0 ? <div className={styles.emptyMsg}>Nothing is paused</div> : null}
					{rows.length > 0 ? (
						<div className={styles.tableWrapper}>
							<table className={styles.table}>
								<thead className={styles.headers}>
									<tr>
										<th>
											<Checkbox
												checked={selectAll}
												onChange={() => {
													handleSelectAll(!selectAll);
												}}
											/>
										</th>
										<th>Campaign ID</th>
										<th>Phase ID</th>
										<th>Domain</th>
										<th>Phase</th>
										<th>Daily gain</th>
										<th>Effective max loss</th>
										<th>Key</th>
									</tr>
								</thead>
								<tbody>
									{rows.map(row => (
										<tr key={row.key}>
											<td className={styles.checkbox}>
												<Checkbox
													checked={row.isSelected}
													onChange={() => {
														setRows(prev =>
															prev.map(item =>
																item.key === row.key ? { ...item, isSelected: !item.isSelected } : item
															)
														);
														setSelectAll(false);
													}}
												/>
											</td>
											<td>{row.campaign_id}</td>
											<td>{row.campaign_phase_id}</td>
											<td>{row.domain}</td>
											<td>{row.phase}</td>
											<td>{row.daily_gain}</td>
											<td>{row.effective_max_loss}</td>
											<td>{row.key}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : null}
				</div>
				<BottomBar>
					{rows.length > 0 ? <div className={styles.selectedCounter}>Selected {selectedCount} item(s)</div> : null}
					<BottomBarActions>
						<PrimaryButton
							disabled={isLoadingStatus || isLoadingClear || selectedCount === 0}
							onClick={handleClearCache}
							isRotateIcon={isLoadingClear}
							icon={isLoadingClear ? 'loading-01' : 'send-03'}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'domain_max_loss_clear_cache')] }}>
							Clear Cache
						</PrimaryButton>
					</BottomBarActions>
				</BottomBar>
			</Page>
		</Disable>
	);
});
